/**
 * Page-agnostic export helpers, shared by the Hudl page and the site-wide
 * table export (`export-dialog.tsx`): readable errors, day formatting, the
 * browser "save this file" step and page-specific file names. No React here.
 */
import { ApiError } from '../../lib/api-error';
import { EXPORT_COPY, EXPORT_LIMIT_MESSAGES, EXPORT_LIMITS } from '../../lib/constants';

/** Error text for anything thrown by the API client — never a stack trace. */
export function toMessage(caught: unknown, fallback: string): string {
  if (ApiError.isApiError(caught)) {
    if (caught.isOffline) return 'You appear to be offline. Check your connection and try again.';
    return caught.message;
  }
  return fallback;
}

/**
 * A failed export in words: the server's 413 already names the limit and the
 * way out; the keyed message is the fallback when the body was not readable.
 */
export function exportErrorMessage(caught: unknown): string {
  if (ApiError.isApiError(caught) && caught.status === 413) {
    const key = caught.details?.limit_key;
    const keyed =
      typeof key === 'string' && key in EXPORT_LIMITS
        ? EXPORT_LIMIT_MESSAGES[key as keyof typeof EXPORT_LIMITS]
        : EXPORT_LIMIT_MESSAGES.cells;
    return caught.message !== '' && /limit/i.test(caught.message) ? caught.message : keyed;
  }
  return toMessage(caught, EXPORT_COPY.failed);
}

/** `2026-09-21` → `Sep 21, 2026` (UTC so the date never shifts a day). */
export function formatDay(value: string | null): string {
  if (value === null || value === '') return '';
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Hand a downloaded blob to the browser as a file save. */
export function saveBlob(blob: Blob, fileName: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** `Varsity vs. Wayne` → `Varsity_vs_Wayne` — safe in every file system. */
export function fileNamePart(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '');
}

/** Local calendar day `YYYY-MM-DD` of an ISO timestamp (today when absent). */
export function fileDay(iso: string | null | undefined, now: Date = new Date()): string {
  const date = iso === null || iso === undefined || iso === '' ? now : new Date(iso);
  if (Number.isNaN(date.getTime())) return fileDay(null, now);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Page-specific file stem: `PlayArchive_Varsity_vs_Wayne_2026-09-25`,
 * `GameSummary_Northmont_2026-09-25`, `Roster_2026-09-28`. The server adds
 * the extension for the chosen file type.
 */
export function exportFileStem(
  pagePrefix: string,
  subject: string | null | undefined,
  day: string
): string {
  return [pagePrefix, fileNamePart(subject), day].filter((part) => part !== '').join('_');
}

/**
 * Every row behind a server-paged table, in the page's own sort + filters:
 * page 1…n at `limit` rows until `total` is reached (bounded by the export row
 * limit so a runaway total can never loop).
 */
export async function fetchAllPages<Row>(
  fetchPage: (page: number, limit: number) => Promise<{ items: readonly Row[]; total: number }>,
  limit: number,
  maxRows = 10_000
): Promise<Row[]> {
  const all: Row[] = [];
  for (let page = 1; all.length < maxRows; page += 1) {
    const result = await fetchPage(page, limit);
    all.push(...result.items);
    if (result.items.length < limit || all.length >= result.total) break;
  }
  return all.slice(0, maxRows);
}
